import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { UtensilsCrossed, Eye, EyeOff, Lock, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenError, setTokenError] = useState('');
    const [maskedEmail, setMaskedEmail] = useState('');
    const [success, setSuccess] = useState(false);
    
    // Form state
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    // Validation
    const passwordMinLength = 8;
    const passwordValid = password.length >= passwordMinLength;
    const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
    const canSubmit = passwordValid && passwordsMatch && !loading;

    // Verify token on mount
    useEffect(() => {
        let mounted = true;
        
        const verifyToken = async () => {
            if (!token) {
                if (mounted) {
                    setTokenValid(false);
                    setTokenError('Lien de réinitialisation invalide');
                    setVerifying(false);
                }
                return;
            }
            
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-reset-token?token=${encodeURIComponent(token)}`
                );
                const data = await response.json();
                
                if (mounted) {
                    if (data.valid) {
                        setTokenValid(true);
                        setMaskedEmail(data.email || '');
                    } else {
                        setTokenValid(false);
                        setTokenError(data.message || 'Lien invalide ou expiré');
                    }
                }
            } catch (error) {
                if (mounted) {
                    setTokenValid(false);
                    setTokenError('Erreur de vérification du lien');
                }
            } finally {
                if (mounted) {
                    setVerifying(false);
                }
            }
        };
        
        verifyToken();
        
        return () => {
            mounted = false;
        };
    }, [token]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        if (!canSubmit) return;
        
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token: token,
                    new_password: password
                })
            });
            
            const data = await response.json();
            
            if (response.ok) {
                setSuccess(true);
                toast.success('Mot de passe mis à jour avec succès');
                // Redirect after 2 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } else {
                toast.error(data.detail || 'Erreur lors de la réinitialisation');
                if (data.detail?.includes('expiré') || data.detail?.includes('invalide')) {
                    setTokenValid(false);
                    setTokenError(data.detail);
                }
            }
        } catch (error) {
            toast.error('Erreur de connexion au serveur');
        } finally {
            setLoading(false);
        }
    }, [canSubmit, token, password, navigate]);

    // Render loading state
    if (verifying) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                    <Card className="border-border/50 shadow-card">
                        <CardContent className="p-8">
                            <div className="flex flex-col items-center justify-center gap-4">
                                <Loader2 className="w-8 h-8 animate-spin text-accent" />
                                <p className="text-muted-foreground">Vérification du lien...</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Render invalid token state
    if (!tokenValid) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <div className="w-full max-w-md animate-fadeIn">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl overflow-hidden bg-accent mb-4">
                            <img src="/icons/logo.png" alt="ALDRIN'S" className="w-20 h-20 object-cover" />
                        </div>
                        <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">
                            ALDRIN'S RESTAURANT
                        </h1>
                    </div>
                    
                    <Card className="border-border/50 shadow-card">
                        <CardContent className="p-6">
                            <div className="text-center">
                                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10 mb-4">
                                    <XCircle className="w-6 h-6 text-destructive" />
                                </div>
                                <h2 className="font-display text-xl font-semibold mb-2">
                                    Lien invalide
                                </h2>
                                <p className="text-sm text-muted-foreground mb-6">
                                    {tokenError}
                                </p>
                                <Button
                                    onClick={() => navigate('/login')}
                                    className="w-full bg-accent hover:bg-accent/90"
                                >
                                    Retour à la connexion
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Render success state
    if (success) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <div className="w-full max-w-md animate-fadeIn">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl overflow-hidden bg-accent mb-4">
                            <img src="/icons/logo.png" alt="ALDRIN'S" className="w-20 h-20 object-cover" />
                        </div>
                        <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">
                            ALDRIN'S RESTAURANT
                        </h1>
                    </div>
                    
                    <Card className="border-border/50 shadow-card">
                        <CardContent className="p-6">
                            <div className="text-center">
                                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-500/10 mb-4">
                                    <CheckCircle className="w-6 h-6 text-green-500" />
                                </div>
                                <h2 className="font-display text-xl font-semibold mb-2">
                                    Mot de passe mis à jour
                                </h2>
                                <p className="text-sm text-muted-foreground mb-2">
                                    Votre mot de passe a été réinitialisé avec succès.
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Redirection vers la connexion...
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Render reset form
    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="w-full max-w-md animate-fadeIn">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl overflow-hidden bg-accent mb-4">
                        <img src="/icons/logo.png" alt="ALDRIN'S" className="w-20 h-20 object-cover" />
                    </div>
                    <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">
                        ALDRIN'S RESTAURANT
                    </h1>
                    <p className="text-muted-foreground mt-1">Gestion de Stock</p>
                </div>

                <Card className="border-border/50 shadow-card">
                    <CardContent className="p-6">
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent/10 mb-3">
                                <Lock className="w-6 h-6 text-accent" />
                            </div>
                            <h2 className="font-display text-xl font-semibold">
                                Nouveau mot de passe
                            </h2>
                            {maskedEmail && (
                                <p className="text-sm text-muted-foreground mt-1">
                                    Pour le compte : {maskedEmail}
                                </p>
                            )}
                        </div>
                        
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="new-password">Nouveau mot de passe</Label>
                                <div className="relative">
                                    <Input
                                        id="new-password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={passwordMinLength}
                                        autoFocus
                                        data-testid="new-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                <p className={`text-xs ${passwordValid ? 'text-green-500' : 'text-muted-foreground'}`}>
                                    {passwordValid ? '✓' : '○'} Minimum {passwordMinLength} caractères
                                </p>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                                <div className="relative">
                                    <Input
                                        id="confirm-password"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        data-testid="confirm-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                {confirmPassword.length > 0 && (
                                    <p className={`text-xs ${passwordsMatch ? 'text-green-500' : 'text-destructive'}`}>
                                        {passwordsMatch ? '✓ Les mots de passe correspondent' : '✗ Les mots de passe ne correspondent pas'}
                                    </p>
                                )}
                            </div>
                            
                            <Button 
                                type="submit" 
                                className="w-full bg-accent hover:bg-accent/90"
                                disabled={!canSubmit}
                                data-testid="reset-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Mise à jour...
                                    </>
                                ) : (
                                    'Mettre à jour le mot de passe'
                                )}
                            </Button>
                        </form>
                        
                        <div className="mt-4 text-center">
                            <button
                                type="button"
                                onClick={() => navigate('/login')}
                                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                            >
                                Retour à la connexion
                            </button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
